<template>
  <MissionHeader v-if="missionInfos" :missionInfos="missionInfos" />

  <div class="container-sm top-content">
    <Spinner v-if="connectingToCable" />

    <template v-else-if="missionFound">
      <MissionCard v-if="missionInfos" :missionInfos="missionInfos"
                                       v-model:missionCardMounted="missionCardMounted" />

      <HoustonMessagerie :maydayChatEnabled="maydayChatEnabled"
                         :userActivities="userActivities"
                         :subscription="subscription"
                         v-model:messagerieMounted="messagerieMounted"
                         :missionInfos="missionInfos"
                         :beneficaryGeolocation="beneficaryGeolocation"
                         :typing="typing"/>

    </template>

    <MissionCardNotFound v-else />
  </div>

  <WebPushModal v-if="showWebPushModal" @enable="enableNotifications" @decline="declineWebPush" />
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import cable from "actioncable";
import Spinner from "./utils/Spinner.vue";
import HoustonMessagerie from "./HoustonMessagerie.vue";
import MissionHeader from "./MissionHeader.vue";
import MissionCard from "./MissionCard.vue";
import MissionCardNotFound from "./MissionCardNotFound.vue";
import WebPushModal from "./WebPushModal.vue";
import { debugMessage, sendAcCommand, isNullOrUndefined, isNotNullOrUndefined } from '@lib/utilities'
import { houstonId, cableUrl } from "@lib/houston";
import { CHANNEL_NAMES, setActiveChannel } from "@lib/chatChannel";
import { supported as webPushSupported, enableWebPush, permissionStatus } from "@lib/WebPush";
import { vapidKey } from "@config/firebase";

const { locale } = useI18n()

const connectingToCable = ref(true)
const userActivities = ref([])
const maydayChatEnabled = ref(false)
const consumer = ref(null)
const subscription = ref(null)
const missionInfos = ref(null)
const missionFound = ref(false)
const initialScroll = ref(false)
const messagerieMounted = ref(false)
const missionCardMounted = ref(false)
const beneficaryGeolocation = ref({lat: null, lng: null})
// Name of the channel that confirmed our subscription, null while both are
// still in the running.
const activeChannel = ref(null)
// Subscriptions created for each candidate channel name, by name, until one of
// them confirms and the others are dropped.
const pendingSubscriptions = {}
// Whether the guided depollution flow is currently "typing" the next message,
// so the front shows the typing animation until the activity arrives.
const typing = ref(false)
const webPushSetup = ref(false)
// Browser notification permission, refreshed after each setup attempt so the
// modal closes once the beneficiary has made a choice.
const notificationPermission = ref(permissionStatus())
// Remembers a refusal so we stop prompting on later reconnects/reloads.
const WEB_PUSH_DISMISSED_KEY = 'mayday:web_push_prompt_dismissed'
const webPushDismissed = ref(readWebPushDismissed())

// The opt-in modal only makes sense when the browser supports Web Push and the
// beneficiary has neither answered the browser prompt ('default') nor declined
// ours. On mobile, requesting the permission requires a user gesture, so we
// drive it from the modal's button rather than prompting on connect.
const showWebPushModal = computed(() =>
  missionFound.value &&
  webPushSupported() &&
  notificationPermission.value === 'default' &&
  !webPushDismissed.value
)

const doInitialScroll = computed(() => {
  // We are waiting response from fetch_conversations command and all the child components
  // to be mounted to be sure we scroll to the real page bottom
  return userActivities.value.length > 0 && messagerieMounted.value && missionCardMounted.value;
})

watch(doInitialScroll, (newVal) => {
  if (newVal && !initialScroll.value) {
    scrollToBottom();
    initialScroll.value = true;
  }
})

// We cannot tell which channel name the server behind cableUrl() exposes, and
// Rails answers nothing at all when the class is unknown (it only logs
// "Subscription class not found"). So we subscribe to both names at once and
// keep whichever confirms, rather than trying them one after the other and
// making every beneficiary wait out a timeout. Unsubscribing the losing name
// leaves one harmless "Unable to find subscription" line in the server log.
const subscribeToChannels = () => {
  CHANNEL_NAMES.forEach((channelName) => {
    pendingSubscriptions[channelName] = subscribeToChannel(channelName);
  });
}

const connectToActionCable = () => {
  consumer.value = cable.createConsumer(cableUrl());

  subscribeToChannels();
}

const subscribeToChannel = (channelName) => {
  return consumer.value.subscriptions.create(
    { channel: channelName, mission_id: houstonId(), locale: locale.value },
    {
      connected: () => onChannelConnected(channelName),
      received: onAcReception,
      disconnected: () => {
        connectingToCable.value = true;
      },
      rejected: () => onChannelRejected(channelName),
    }
  );
}

const onChannelConnected = (channelName) => {
  if (isNullOrUndefined(activeChannel.value)) {
    // First confirmation wins the race and tells us which channel this server
    // speaks, which sendAcCommand needs to name its commands accordingly.
    activeChannel.value = channelName;
    subscription.value = pendingSubscriptions[channelName];
    setActiveChannel(channelName);
    dropPendingSubscriptions({ kept: channelName });
  } else if (activeChannel.value !== channelName) {
    // Only a server exposing both names could confirm twice: ignore the loser.
    return
  }

  // Also runs again after a reconnection, to refresh the feed we may have
  // missed while the socket was down.
  connectingToCable.value = false;
  missionFound.value = true;

  sendAcCommand(subscription.value, 'fetch_conversation')
  sendAcCommand(subscription.value, 'mission_infos')
  // Re-register the token silently only when permission was already
  // granted (no prompt, so no user gesture needed). When it is still
  // 'default', the enable button drives the gesture-bound request.
  if (notificationPermission.value === 'granted') setupWebPush()
}

// A rejection means the server does know this channel — hence not the other
// name — and turned the mission down, so nothing else is coming: we can show
// MissionCardNotFound without waiting for the other subscription.
const onChannelRejected = (channelName) => {
  if (isNotNullOrUndefined(activeChannel.value)) {
    return
  }

  debugMessage(`Subscription to ${channelName} rejected`, 'beneficiaryChannel');
  dropPendingSubscriptions({ alreadyDropped: channelName });
  connectingToCable.value = false;
}

// Ends the race: drops the subscriptions we are not going to use and forgets
// them all, `subscription` being the only reference we need from then on.
// `kept` is the channel we keep talking through, `alreadyDropped` one
// ActionCable forgot on its own (a rejected subscription), which must not be
// sent an unsubscribe command the server cannot match.
const dropPendingSubscriptions = ({ kept, alreadyDropped } = {}) => {
  Object.entries(pendingSubscriptions).forEach(([channelName, pendingSubscription]) => {
    if (channelName !== kept && channelName !== alreadyDropped) {
      pendingSubscription.unsubscribe();
    }

    delete pendingSubscriptions[channelName];
  });
}

// Requests notification permission (when needed) and registers the resulting
// FCM token with the backend, so the beneficiary gets native notifications
// when this chat tab is backgrounded. No-ops on browsers that do not support
// Web Push (e.g. iOS Safari outside a PWA install). The `webPushSetup` guard
// keeps it to one successful registration; it is only set once a token has
// actually been registered, so a refusal or a transient error lets a later
// attempt retry. Must run inside a user gesture when permission is still
// 'default' (mobile requirement) — see enableNotifications.
const setupWebPush = async () => {
  if (webPushSetup.value || !webPushSupported()) {
    return
  }

  await enableWebPush({
    vapidKey,
    onToken: (token) => {
      sendAcCommand(subscription.value, 'register_web_push_token', { token })
      webPushSetup.value = true
    }
  })
  // Reflect the answer so the enable button hides once granted or denied.
  notificationPermission.value = permissionStatus()
}

// Entry point bound to the modal's "enable" click: the user gesture is what
// lets Notification.requestPermission actually prompt on mobile.
const enableNotifications = () => setupWebPush()

// Beneficiary refused our modal: close it and remember the refusal so we do
// not nag on every reconnect/reload (the browser permission stays 'default').
const declineWebPush = () => {
  webPushDismissed.value = true
  writeWebPushDismissed()
}

// localStorage may be unavailable (private mode, test env): never let it throw.
function readWebPushDismissed() {
  try {
    return localStorage.getItem(WEB_PUSH_DISMISSED_KEY) === 'true'
  } catch {
    return false
  }
}

function writeWebPushDismissed() {
  try {
    localStorage.setItem(WEB_PUSH_DISMISSED_KEY, 'true')
  } catch {
    // Ignore: dismissal simply won't persist across reloads.
  }
}

const updateBeneficiaryGeolocation = () => {
  const beneficaryGeolocationData = userActivities.value.find((userActivity) => {
    return userActivity.category === 'beneficiary_geolocation'
  })?.data

  if (isNotNullOrUndefined(beneficaryGeolocationData)) {
    beneficaryGeolocation.value = { lat: beneficaryGeolocationData.latitude, lng: beneficaryGeolocationData.longitude }
  }
}

const onAcReception = (data) => {
  switch (data.action) {
    case 'fetch_conversation':
      userActivities.value = data.response.user_activities
      maydayChatEnabled.value = data.response.mayday_chat
      updateBeneficiaryGeolocation()
      scrollToBottom()
      break
    case 'mission_infos':
      missionInfos.value = data.response;
      break
    // The legacy BeneficiaryChannel spells the action without the second 'i'.
    case 'update_beneficary_geolocation':
    case 'update_beneficiary_geolocation':
      const userActivity = data.response

      updateUserActivity(userActivity);
      updateBeneficiaryGeolocation()
      break
    case 'update_truck_geolocation':
      updateUserActivity(data.response);
      break;
    case 'typing':
      typing.value = true
      scrollToBottom()
      break
    case 'new_activity':
      onNewActivityReception(data.response)
    break
      default:
      debugMessage(`Received ${data.action}`, 'beneficiaryChannel');
      break
  }
}

const updateUserActivity = (newUserActivity) => {
  const index = userActivities.value.findIndex(userActivity => userActivity.id === newUserActivity.id);
  // If the userActivty exists with replace we just update it otherwise we add it.
  if (index === -1) {
    userActivities.value.push(newUserActivity)
  } else {
    userActivities.value[index] = newUserActivity
  };
}

const onNewActivityReception = (userActivity) => {
  typing.value = false
  userActivities.value.push(userActivity)
  readMessage(userActivity)
  scrollToBottom()
}

const readMessage = (userActivity) => {
  if (userActivity.category === 'truck_message') {
    sendAcCommand(subscription.value, 'read_message', { activity_id: userActivity.id })
  }
}

// Wait for the newly received message to render before scrolling, otherwise
// scrollHeight is still the pre-render value and we stop short of the last message.
const scrollToBottom = () => {
  nextTick(() => {
    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
  });
}

onMounted(() => {
  connectToActionCable();
})
</script>

